import { useSpace } from '@/store/modules/App/space'
import { defineStore } from 'pinia'
import { orderBy } from 'lodash'
import { client } from '@/services/client'
import type { PresetFilter } from '@/types/filters'
import type {
  ParsePresetFilterCreatePayloadInput,
  ParsePresetFilterCreatePayloadOutput,
} from '@/components/ContentManager/ListStories/helpers'

const STORAGE_PRESET_FILTER_KEY = 'preset_filter'

export type State = {
  presetFilters: PresetFilter[] | undefined
}

export const useFilters = defineStore('filters', {
  state: (): State => ({
    presetFilters: undefined,
  }),
  getters: {
    getPresetFilters: (state: State): PresetFilter[] =>
      orderBy(state.presetFilters, 'id'),
    getPreset:
      (state: State) =>
      (presetId: number): PresetFilter | undefined =>
        state.presetFilters?.find((preset) => preset.id === +presetId),
    getPresetFilterFromStorage: (): PresetFilter | null | number => {
      const storagePresetFilterKey = localStorage.getItem(
        STORAGE_PRESET_FILTER_KEY,
      )
      if (storagePresetFilterKey) {
        return parseInt(storagePresetFilterKey)
      }
      return null
    },
  },
  actions: {
    setPresetFilters(filters: PresetFilter[]): void {
      this.presetFilters = filters
    },

    pushNewPresetFilter(filter: PresetFilter): void {
      this.presetFilters?.push(filter)
    },

    updatePresetFilterOnState(filter: PresetFilter): void {
      const index = this.presetFilters?.findIndex(
        (preset) => preset.id === filter.id,
      )
      if (typeof index !== 'undefined') {
        this.presetFilters?.splice(index, 1, filter)
      }
    },

    deletePresetFilterLocal(presetId: number): void {
      const index = this.presetFilters?.findIndex(
        (preset) => preset.id === presetId,
      )

      if (typeof index !== 'undefined') {
        this.presetFilters?.splice(index, 1)
      }
    },

    clearPresetFilters(): void {
      this.presetFilters = []
    },

    async createPresetFilter({
      spaceId,
      payload,
    }: {
      spaceId: string | number
      payload: ParsePresetFilterCreatePayloadInput
    }): Promise<PresetFilter> {
      const { data } = await client.post(
        `spaces/${spaceId}/preset_filters`,
        payload,
        {
          region: useSpace().region,
        },
      )

      this.pushNewPresetFilter(data.preset_filter)

      return data.preset_filter
    },

    async loadPresetFilters(payload: {
      spaceId: string | number
    }): Promise<PresetFilter[]> {
      const { spaceId } = payload

      const { data } = await client.get(`/spaces/${spaceId}/preset_filters`, {
        region: useSpace().region,
      })

      this.setPresetFilters(data.preset_filters)
      return data.preset_filters
    },

    async updatePresetFilter({
      spaceId,
      presetId,
      payload,
    }: {
      spaceId: string | number
      presetId: number
      payload: ParsePresetFilterCreatePayloadOutput
    }): Promise<void> {
      const { data } = await client.put(
        `spaces/${spaceId}/preset_filters/${presetId}`,
        payload,
        {
          region: useSpace().region,
        },
      )

      this.updatePresetFilterOnState(data.preset_filter)
    },

    async deletePresetFilter({
      spaceId,
      presetId,
    }: {
      spaceId: string | number
      presetId: number
    }): Promise<void> {
      await client.delete(`spaces/${spaceId}/preset_filters/${presetId}`, {
        region: useSpace().region,
      })

      this.deletePresetFilterLocal(presetId)
    },
  },
})
